import { useEffect, useState } from 'react';
import NavigationMenuItem from '../NavigationMenuItem';
import cc from 'classcat';
import { useSelector } from 'react-redux';
import { dispatch, RootState } from '../../store';
import { Link } from 'react-router-dom';
import {
    INavigationItem,
    closeAllDropdowns,
    selectMobileSideBar,
    selectNavigationData,
    selectSideBarCollapsed,
    updateMobileSideBarOpen,
    updateSideBarCollapsed,
} from '../../store/slices/navigation/navigation.slice';
import { LocalStorage, NAVIGATION_SECTION_STATES } from '../../util/LocalStorage';
import { diContainer } from '../../libs/container';
import { useFeatureFlag } from '../../hooks/featureFlags/useFeatureFlags';
import { Feature } from '@cyberlabrs/helpers';
import { AppConfig } from '../../config/AppConfig';
import SubscriptionReminder from '../SubscriptionReminder';
import { selectAuth } from '../../store/slices/auth.slice';
import { RoleTypeEnum } from '../../commonTypes/user.types';

const SECTION_WITHOUT_TITLE = '';

interface ISectionState {
    [sectionTitle: string]: boolean;
}

const Navigation = () => {
    const localStorage = diContainer.get(LocalStorage);
    const navigationItems = useSelector(selectNavigationData);
    const collapsed = useSelector(selectSideBarCollapsed);
    const mobileMenuOpen = useSelector(selectMobileSideBar);
    const invoicingEnabled = useFeatureFlag(Feature.INVOICING);
    const billingEnabled = useFeatureFlag('BILLING');
    const authUser = useSelector(selectAuth);

    const organizationId = useSelector<RootState, string>(state => state.auth.value.organizationId);
    const cyberlabOrganizationId = AppConfig.getCyberlabOrganizationId;
    const isCyberlab = organizationId === cyberlabOrganizationId;

    //this needs to be deleted when removing feature flag
    const filteredItems = navigationItems
        .filter(item => {
            if (item.keyTitle === 'invoicing' && !invoicingEnabled) return false;
            if (item.keyTitle === 'performance' && !isCyberlab) return false;
            if (item.keyTitle === 'subscription' && !billingEnabled) return false;
            return true;
        })
        .map(item => {
            // Also filter submenu items based on feature flags
            const filteredSubMenuItems = item.subMenuItems.filter(subItem => {
                if (subItem.keyTitle === 'subscription' && !billingEnabled) return false;
                return true;
            });
            return {
                ...item,
                subMenuItems: filteredSubMenuItems,
            };
        });

    const groupedNavigationItems: Record<string, INavigationItem[]> = filteredItems.reduce((acc, item) => {
        const sectionTitle = item.sectionTitle || '';
        if (!acc[sectionTitle]) {
            acc[sectionTitle] = [];
        }
        acc[sectionTitle].push(item);
        return acc;
    }, {} as Record<string, INavigationItem[]>);

    const storageState = localStorage.get(NAVIGATION_SECTION_STATES);
    const DEFAULT_NAVIGATION_SECTION_STORAGE_STATE: ISectionState = {};

    for (const item in groupedNavigationItems) {
        if (item) DEFAULT_NAVIGATION_SECTION_STORAGE_STATE[item] = false;
    }

    const [openSections, setOpenSections] = useState<ISectionState>(
        storageState ? JSON.parse(storageState) : DEFAULT_NAVIGATION_SECTION_STORAGE_STATE
    );

    const toggleSection = (sectionTitles: string[], newState?: boolean) => {
        setOpenSections(prevState => {
            const updatedSections = { ...prevState };
            sectionTitles.forEach(sectionTitle => {
                updatedSections[sectionTitle] = newState ?? !prevState[sectionTitle];
            });
            return updatedSections;
        });
    };

    useEffect(() => {
        localStorage.set(NAVIGATION_SECTION_STATES, openSections);
    }, [openSections]);

    useEffect(() => {
        const handleBeforeUnload = () => {
            dispatch(closeAllDropdowns());
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, []);

    return (
        <div className={cc(['navigation', { 'navigation--collapsed': collapsed }, { 'navigation--open': mobileMenuOpen }])}>
            <button className="navigation__exit" onClick={() => dispatch(updateMobileSideBarOpen(false))}>
                <img src="/svg/sidebar/exit.svg" alt="exit" />
            </button>
            <Link
                to="/"
                className={cc(['navigation__logo', { 'navigation__logo--collapsed': collapsed }])}
                onClick={() => mobileMenuOpen && dispatch(updateMobileSideBarOpen(false))}
            >
                <img src={collapsed ? '/svg/fornul.io-logo.svg' : '/svg/fornul.io-nav-logo.svg'} alt="fornul" />
            </Link>
            <div
                className="navigation__menu"
                onScroll={() => {
                    if (collapsed) dispatch(closeAllDropdowns());
                }}
            >
                {Object.keys(groupedNavigationItems).map(sectionTitle => (
                    <div key={sectionTitle} className="navigation__menu-section">
                        {sectionTitle !== SECTION_WITHOUT_TITLE && (
                            <button
                                className={cc(['navigation__menu-section--title', { 'navigation__menu-section--title--collapsed': collapsed }])}
                                onClick={() => toggleSection([sectionTitle])}
                            >
                                {collapsed ? null : sectionTitle}
                            </button>
                        )}
                        <div className="navigation__menu-section--list">
                            {groupedNavigationItems[sectionTitle].map(item => (
                                <NavigationMenuItem key={item.url} {...item} collapsed={collapsed} />
                            ))}
                        </div>
                    </div>
                ))}
                <button
                    onClick={() => {
                        const allSectionTitles = Object.keys(groupedNavigationItems);
                        toggleSection(allSectionTitles, true);
                        dispatch(closeAllDropdowns());
                        dispatch(updateSideBarCollapsed(!collapsed));
                    }}
                    className={cc(['navigation__collapse', { 'navigation__collapse--left': !collapsed }])}
                >
                    <img
                        src="/img/common/arrow-left-white.svg"
                        alt="Arrow in circle"
                        className={cc(['navigation__collapse-img', collapsed && 'navigation__collapse-img--rotate'])}
                    />
                </button>
            </div>
            {authUser.role?.name === RoleTypeEnum.OWNER && billingEnabled && (
                <div className="navigation__bottom">
                    <SubscriptionReminder />
                </div>
            )}
        </div>
    );
};

export default Navigation;
